import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { partition } from 'lodash';

import {
  CreateMediumBody,
  isImage,
  Medium,
  useCreateMedium,
  useDeleteMedium,
} from '@/models/medium';
import { useMedia } from '@/models/medium';
import { HttpError } from '@/utils/error/HttpError';

import { extractLoadingImageAndFileUploads } from './extractLoadingImageAndFileUploads';

export type FileWithMediumId = { file: File; mediumId?: string | null };
export type FileWithError = { file: File; error?: HttpError };

export const useFileUploader = ({
  uploadData,
  media: mediaFromProps = [],
  onChange,
  onUpload,
  onDelete,
  deleteWithApi,
  headers,
}: {
  uploadData: CreateMediumBody;
  media?: Medium[];
  onChange?: Dispatch<SetStateAction<Medium[]>>;
  onUpload?: (medium: Medium) => void;
  onDelete?: (mediumId: string) => void;
  deleteWithApi?: boolean;
  headers?: HeadersInit;
}) => {
  const { mutateAsync: createMedium, error: createMediumError } = useCreateMedium(headers);
  const { mutateAsync: deleteMedium } = useDeleteMedium(uploadData.articleId);

  const [loadingOrSucceededUploads, setLoadingOrSucceededUploads] = useState<FileWithMediumId[]>(
    []
  );
  const [failedUploads, setFailedUploads] = useState<FileWithError[]>([]);

  const { setPolling, media } = useKeepMediaDataUpdated({
    mediaFromProps,
    uploadData,
  });

  const { images, otherFiles } = separateImagesAndOtherFiles(media);

  const handleCloseFailedUpload = (indexToDelete: number) => {
    setFailedUploads(failedUploads.filter((_, index) => indexToDelete !== index));
  };

  const handleUpload = async (files: File[]) => {
    const newLoadingUploads = files.map((file) => ({ file, mediumId: null }));

    setLoadingOrSucceededUploads((current) => [...current, ...newLoadingUploads]);

    await Promise.all(
      files.map(async (file) => {
        try {
          const medium = await createMedium({ file: file, ...uploadData });

          setLoadingOrSucceededUploads((current) =>
            addMediumIdToUploadSucceeded(current, medium, file)
          );

          if (onChange) {
            onChange((uploadedMedias) => [medium, ...uploadedMedias]);
          }

          if (onUpload) {
            onUpload(medium);
          }
        } catch (e) {
          console.error(`Error uploading ${file.name}:`, e);

          setLoadingOrSucceededUploads((current) =>
            current.filter((loadingFile) => loadingFile.file !== file)
          );

          setFailedUploads((current) => [...current, { file, error: e as HttpError }]);
        }
      })
    );
  };

  const handleDelete = async (idToDelete: string) => {
    if (deleteWithApi) {
      await deleteMedium(idToDelete);
    }

    setLoadingOrSucceededUploads((current) =>
      current.filter((file) => file.mediumId !== idToDelete)
    );

    if (onChange) {
      onChange((prev) => prev.filter(({ id }) => id !== idToDelete));
    }

    if (onDelete) {
      onDelete(idToDelete);
    }
  };

  const { loadingUploads, loadingImageUploads, loadingFileUploads } =
    extractLoadingImageAndFileUploads(loadingOrSucceededUploads, media);

  return {
    images,
    loadingImageUploads,
    otherFiles,
    loadingFileUploads,
    loadingOrSucceededUploads,
    loadingUploads,
    failedUploads,
    createMediumError,
    handleCloseFailedUpload,
    handleUpload,
    handleDelete,
    startPolling: () => setPolling(true),
  };
};

const useKeepMediaDataUpdated = ({
  mediaFromProps,
  uploadData,
}: {
  mediaFromProps: Medium[];
  uploadData: CreateMediumBody;
}) => {
  const queryClient = useQueryClient();
  const [polling, setPolling] = useState(false);
  const { data: pollingData } = useMedia(
    { ...uploadData, types: [uploadData.type] },
    {
      enabled: !!polling,
      refetchInterval: 5000,
    }
  );

  const media = pollingData?.media ?? mediaFromProps;

  const isPollingOnArticle =
    !!uploadData.articleId ||
    !!uploadData.articleConditionId ||
    !!uploadData.articleDefectId ||
    !!uploadData.articleActionId;

  const isPollingOnInvoice = !!uploadData.invoiceId;

  useEffect(() => {
    if (!polling || pollingData?.media?.length === undefined) {
      return;
    }

    if (isPollingOnArticle) {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    } else if (isPollingOnInvoice) {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  }, [queryClient, pollingData?.media?.length, polling, isPollingOnArticle, isPollingOnInvoice]);

  return {
    polling,
    setPolling,
    media,
  };
};

const addMediumIdToUploadSucceeded = (
  loadingUploads: FileWithMediumId[],
  medium: Medium,
  file: File
): FileWithMediumId[] => {
  return loadingUploads.map((loadingUpload) => {
    if (loadingUpload.file === file) {
      return { ...loadingUpload, mediumId: medium.id };
    }

    return loadingUpload;
  });
};

const separateImagesAndOtherFiles = (
  media: Medium[]
): { images: Medium[]; otherFiles: Medium[] } => {
  const [images, otherFiles] = partition(media, (medium) => isImage(medium));

  return { images, otherFiles };
};
